// Utility functions for handling image URLs

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';

/**
 * Constructs the full image URL from a relative image path
 * @param {string} imageUrl - The relative image URL (e.g., '/api/files/images/products/filename.jpg')
 * @returns {string} - The full image URL
 */
export const getFullImageUrl = (imageUrl) => {
  if (!imageUrl) {
    return null;
  }
  
  // If it's already a full URL, return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  
  // If it starts with /api, construct the full URL with the base URL (without /api suffix)
  // since the backend context path is already /api
  if (imageUrl.startsWith('/api/')) {
    const baseUrl = API_BASE_URL.replace('/api', '');
    return `${baseUrl}${imageUrl}`;
  }
  
  // If it doesn't start with /, add it
  if (!imageUrl.startsWith('/')) {
    imageUrl = '/' + imageUrl;
  }
  
  return `${API_BASE_URL}${imageUrl}`;
};

// Maps a requested image size to the backend response field that carries it.
// The backend (ImageUrlService) always populates these fields - with a real
// Cloudinary URL when available, or the existing EC2 URL as an automatic
// fallback - so preferring them here is always safe, even when Cloudinary is
// disabled or a particular image was never uploaded to Cloudinary.
const PRIMARY_IMAGE_VARIANT_FIELDS = {
  small: 'primarySmallThumbnailImage',
  thumbnail: 'primaryThumbnailImage',
  medium: 'primaryMediumImage',
  original: 'primaryOriginalImage',
};

const IMAGE_VARIANT_FIELDS = {
  small: 'smallThumbnailImage',
  thumbnail: 'thumbnailImage',
  medium: 'mediumImage',
  original: 'originalImage',
};

/**
 * Gets the primary image URL for a product
 * @param {Object} product - The product object
 * @param {boolean} useFallback - Whether to return fallback image if no images available (default: false)
 * @param {'thumbnail'|'medium'|'original'} variant - Preferred optimized size; falls
 *   back to the plain primaryImageUrl/imageUrl when the variant field isn't present
 *   (e.g. responses that don't go through ImageUrlService yet). Default 'medium' is a
 *   good general-purpose size for cards, lists and avatars.
 * @returns {string|null} - The full primary image URL, fallback image, or null
 */
export const getProductPrimaryImageUrl = (product, useFallback = false, variant = 'medium') => {
  if (!product) {
    return useFallback ? getProductFallbackImage() : null;
  }

  const variantField = PRIMARY_IMAGE_VARIANT_FIELDS[variant];
  if (variantField && product[variantField]) {
    return getFullImageUrl(product[variantField]);
  }

  // Check for primaryImageUrl first
  if (product.primaryImageUrl) {
    return getFullImageUrl(product.primaryImageUrl);
  }

  // Fallback to first image in images array
  if (product.images && product.images.length > 0) {
    const firstImage = product.images[0];
    const imageVariantField = IMAGE_VARIANT_FIELDS[variant];
    if (imageVariantField && firstImage[imageVariantField]) {
      return getFullImageUrl(firstImage[imageVariantField]);
    }
    if (firstImage.imageUrl) {
      return getFullImageUrl(firstImage.imageUrl);
    }
  }

  // Return fallback image if requested
  return useFallback ? getProductFallbackImage() : null;
};

/**
 * Gets all image URLs for a product
 * @param {Object} product - The product object
 * @param {'thumbnail'|'medium'|'original'} variant - Preferred optimized size; falls
 *   back to the plain imageUrl when the variant field isn't present. Default
 *   'original' suits full-size gallery/detail views.
 * @returns {Array} - Array of full image URLs
 */
export const getProductImageUrls = (product, variant = 'original') => {
  if (!product || !product.images) {
    return [];
  }

  const variantField = IMAGE_VARIANT_FIELDS[variant];

  return product.images
    .map(image => (variantField && image[variantField]) || image.imageUrl)
    .filter(Boolean)
    .map(getFullImageUrl);
};

/**
 * Gets the fallback image URL (mos-logo.jpg) when no product images are available
 * @returns {string} - The fallback image URL
 */
export const getProductFallbackImage = () => {
  return '/mos-logo.jpg';
};

/**
 * Checks if a product has any images available
 * @param {Object} product - The product object
 * @returns {boolean} - True if product has images, false otherwise
 */
export const hasProductImages = (product) => {
  if (!product) {
    return false;
  }
  
  // Check primaryImageUrl
  if (product.primaryImageUrl) {
    return true;
  }
  
  // Check images array
  if (product.images && product.images.length > 0) {
    return product.images.some(image => image.imageUrl);
  }
  
  return false;
};